import React, { useEffect, useRef, useState } from "react";

// Modules
import moment from "moment";
import TimeAgo from "react-timeago";
import DOMPurify from "dompurify";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";

// UI components
import {
  Avatar,
  Button,
  CircularProgress,
  IconButton,
  Typography,
} from "@mui/material";
import SendIcon from "@mui/icons-material/SendOutlined";
import CloseIcon from "@mui/icons-material/CloseOutlined";
import AttachIcon from "@mui/icons-material/AttachFileOutlined";
import FileIcon from "@mui/icons-material/InsertDriveFileOutlined";
import PersonIcon from "@mui/icons-material/PersonOutlineOutlined";

// Minimal, chat-appropriate toolbar (matches the backend Xss allowlist).
const EDITOR_CONFIG = {
  toolbar: ["bold", "italic", "link", "bulletedList", "numberedList", "blockQuote"],
  placeholder: "Write a message…",
};

// Second line of defence: the backend sanitises on save, we sanitise on render.
const SANITIZE = {
  ALLOWED_TAGS: ["a", "b", "strong", "i", "em", "u", "p", "br", "ul", "ol", "li", "blockquote"],
  ALLOWED_ATTR: ["href", "target", "rel"],
};

const MAX_ATTACHMENTS = 5;
const ACCEPT = "image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip";

// Stable per-person avatar colour derived from their name/email, so each
// participant reads consistently down the thread (forum-style).
const stringToColor = (str) => {
  const seed = (str || "?").trim() || "?";
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = seed.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue}, 45%, 45%)`;
};

// Up to two initials from a name (falls back to the email's first letter).
const getInitials = (name, email) => {
  const source = (name || "").trim();
  if (source) {
    const parts = source.split(/\s+/);
    if (parts.length === 1) {
      return parts[0].charAt(0).toUpperCase();
    }
    return (
      parts[0].charAt(0) + parts[parts.length - 1].charAt(0)
    ).toUpperCase();
  }
  const mail = (email || "").trim();
  return mail ? mail.charAt(0).toUpperCase() : "";
};

const humanSize = (bytes) => {
  if (!bytes) return "";
  const units = ["B", "KB", "MB", "GB"];
  let n = bytes;
  let i = 0;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i += 1;
  }
  return `${n.toFixed(n >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
};

const bodyHasText = (html) =>
  html
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .trim() !== "";

/**
 * Presentational message thread shared by the buyer portal and the seller
 * dashboard. Data fetching/posting lives in the callers - this component
 * renders the thread and a rich composer.
 *
 * `mine` decides which side is highlighted: 'seller' on the dashboard, 'buyer'
 * in the portal. Attachments are optional: when `uploadFile` + `attachmentUrl`
 * + `canUpload` are supplied the composer shows an attach button and messages
 * render their files (images inline, other files as download links).
 */
export default function RoomThread(props) {
  const {
    messages,
    onSend,
    canPost = true,
    mine = "buyer",
    owner = "client",
    identitySlot = null,
    scope = null,
    emptyText = "No messages yet - start the conversation.",
    uploadFile = null,
    attachmentUrl = null,
    canUpload = false,
  } = props;

  const [draft, setDraft] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [sendError, setSendError] = useState(null);
  const endRef = useRef(null);
  const fileInputRef = useRef(null);
  const editorRef = useRef(null);

  useEffect(() => {
    if (endRef.current) {
      endRef.current.scrollIntoView({ block: "end" });
    }
  }, [messages.length]);

  const send = () => {
    const body = draft.trim();
    const fids = attachments.map((a) => a.fid);
    if ((!bodyHasText(body) && fids.length === 0) || isSending || uploading) {
      return;
    }

    setIsSending(true);
    setSendError(null);

    Promise.resolve(onSend(body, fids))
      .then(() => {
        setDraft("");
        editorRef.current?.setData("");
        setAttachments([]);
        setIsSending(false);
      })
      .catch((error) => {
        setIsSending(false);
        setSendError(
          error?.message || "Your message couldn't be sent. Please try again."
        );
      });
  };

  const handleFiles = (fileList) => {
    if (!uploadFile) return;
    const room = MAX_ATTACHMENTS - attachments.length;
    const files = Array.from(fileList).slice(0, room);
    if (!files.length) return;

    setUploading(true);
    setSendError(null);
    Promise.all(files.map((file) => uploadFile(file).catch(() => null)))
      .then((results) => {
        const ok = results.filter(Boolean);
        if (ok.length < files.length) {
          setSendError("Some files couldn't be attached (type or size).");
        }
        setAttachments((prev) => [...prev, ...ok].slice(0, MAX_ATTACHMENTS));
        setUploading(false);
      })
      .catch(() => setUploading(false));
  };

  const removeAttachment = (fid) =>
    setAttachments((prev) => prev.filter((a) => a.fid !== fid));

  const renderAttachments = (list) => {
    if (!list || list.length === 0 || !attachmentUrl) return null;
    return (
      <div className="room-thread__attachments">
        {list.map((att) =>
          att.is_image ? (
            <a
              key={att.fid}
              className="room-thread__att-image"
              href={attachmentUrl(att.fid)}
              target="_blank"
              rel="noreferrer"
            >
              <img src={attachmentUrl(att.fid)} alt={att.filename} />
            </a>
          ) : (
            <a
              key={att.fid}
              className="room-thread__att-file"
              href={attachmentUrl(att.fid)}
              target="_blank"
              rel="noreferrer"
              download={att.filename}
            >
              <FileIcon fontSize="small" />
              <span className="room-thread__att-name">{att.filename}</span>
              <span className="room-thread__att-size">{humanSize(att.size)}</span>
            </a>
          )
        )}
      </div>
    );
  };

  return (
    <div className="room-thread">
      <div className="room-thread__list">
        {messages.length === 0 && (
          <Typography owner={owner} variant="body2" className="room-thread__empty">
            {emptyText}
          </Typography>
        )}
        {messages.map((message) => {
          const isMine =
            (mine === "seller" && message.is_seller) ||
            (mine === "buyer" && !message.is_seller);
          const displayName = message.author_name || message.author_email || "Someone";
          const created = moment.unix(message.created);
          return (
            <div
              className={`room-thread__message ${
                isMine ? "room-thread__message--mine" : ""
              } ${message.is_seller ? "room-thread__message--seller" : ""}`}
              key={`msg-${message.id}`}
            >
              <Avatar
                owner={owner}
                className="room-thread__avatar"
                sx={{ bgcolor: stringToColor(message.author_name || message.author_email) }}
              >
                {getInitials(message.author_name, message.author_email) || (
                  <PersonIcon fontSize="small" />
                )}
              </Avatar>
              <div className="room-thread__content">
                <div className="room-thread__meta">
                  <Typography owner={owner} variant="caption" className="room-thread__author">
                    {displayName}
                    {message.author_name && message.author_email && (
                      <span className="room-thread__author-email">
                        {" "}
                        ({message.author_email})
                      </span>
                    )}
                  </Typography>
                  {message.is_seller && (
                    <span className="room-thread__badge">Seller</span>
                  )}
                  <Typography
                    owner={owner}
                    variant="caption"
                    className="room-thread__time"
                    title={created.format("DD MMM YYYY, HH:mm")}
                  >
                    <TimeAgo date={created.toDate()} />
                  </Typography>
                </div>
                <div
                  className={`room-thread__bubble ${
                    message.kind === "request" ? "room-thread__bubble--request" : ""
                  }`}
                >
                  {message.kind === "request" && (
                    <span className="room-thread__chip">Content request</span>
                  )}
                  {message.body && bodyHasText(message.body) && (
                    <div
                      className="room-thread__body"
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(message.body, SANITIZE),
                      }}
                    />
                  )}
                  {renderAttachments(message.attachments)}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>

      {canPost && (
        <div className="room-thread__composer">
          {scope && (
            <div className="room-thread__scope mb-2">
              <Typography owner={owner} variant="body2">
                {scope.label}
              </Typography>
              <IconButton size="small" onClick={scope.onClear} title="Clear">
                <CloseIcon fontSize="small" />
              </IconButton>
            </div>
          )}
          {identitySlot}
          <div className="room-thread__editor">
            <CKEditor
              editor={ClassicEditor}
              config={EDITOR_CONFIG}
              data={draft}
              onReady={(editor) => {
                editorRef.current = editor;
              }}
              onChange={(event, editor) => setDraft(editor.getData())}
            />
          </div>

          {attachments.length > 0 && (
            <div className="room-thread__pending">
              {attachments.map((att) => (
                <span className="room-thread__pending-chip" key={att.fid}>
                  <FileIcon fontSize="small" />
                  <span className="room-thread__att-name">{att.filename}</span>
                  <IconButton
                    size="small"
                    onClick={() => removeAttachment(att.fid)}
                    title="Remove"
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </span>
              ))}
            </div>
          )}

          <div className="room-thread__composer-actions d-flex gap-2 align-items-center">
            {canUpload && uploadFile && (
              <>
                <input
                  type="file"
                  ref={fileInputRef}
                  hidden
                  multiple
                  accept={ACCEPT}
                  onChange={(e) => {
                    handleFiles(e.target.files);
                    e.target.value = "";
                  }}
                />
                <Button
                  variant="outlined"
                  owner={owner}
                  className={owner === "client" ? "buyer-room__btn buyer-room__btn--outlined" : ""}
                  disabled={uploading || attachments.length >= MAX_ATTACHMENTS}
                  onClick={() => fileInputRef.current?.click()}
                  startIcon={
                    uploading ? <CircularProgress size={16} /> : <AttachIcon />
                  }
                >
                  <Typography owner={owner} variant="button">
                    Attach
                  </Typography>
                </Button>
              </>
            )}
            <Button
              variant="contained"
              owner={owner}
              className={`room-thread__send ${
                owner === "client" ? "buyer-room__btn" : ""
              }`}
              disabled={isSending || uploading}
              onClick={send}
              startIcon={<SendIcon />}
            >
              <Typography owner={owner} variant="button">
                Send
              </Typography>
            </Button>
          </div>

          {sendError && (
            <Typography owner={owner} variant="caption" color="error" className="d-block mt-1">
              {sendError}
            </Typography>
          )}
          <Typography owner={owner} variant="caption" className="room-thread__note">
            Everyone in this room is notified by email when you post. Email
            replies are not added to the conversation.
          </Typography>
        </div>
      )}
    </div>
  );
}
